/**
 * The small chip that opens an edit form, placed beside the thing it edits.
 *
 * **It moved for a reason worth keeping.** Edit buttons used to sit in the
 * row of actions at the top right, next to "New deliverable" and "New task" —
 * so the group mixed *change this thing* with *make a different thing*, and
 * the most common mistake on a project page was pressing the wrong one.
 *
 * Putting it against the heading says what it edits without a word of
 * explanation. That is also why it must never be grouped with create actions
 * again: the position IS the label.
 *
 * `CHANGE ✎` is the same control under a different word, for the places where
 * the thing being edited is a person rather than a record — changing who the
 * PM is reads oddly as "edit".
 */
export function EditChip({
  label = "EDIT",
  title,
  onClick,
  pressed,
}: {
  /** `EDIT`, `CHANGE`, or `MINIMIZE` for the project panel. */
  label?: string;
  /** What this edits, for anyone who cannot see where the chip sits. */
  title: string;
  onClick: () => void;
  /** Whether the form it toggles is currently open. */
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      className="edit-chip"
      onClick={onClick}
      title={title}
      aria-label={title}
      // A toggle, not an action: it opens and closes the same form, and a
      // screen reader should say which state it is in rather than announcing
      // "edit" twice and leaving the user to find out.
      aria-pressed={pressed}
    >
      {label} <span aria-hidden="true">✎</span>
    </button>
  );
}
